import React, { SyntheticEvent, useEffect, useState } from 'react';
import { FormControl, InputLabel, MenuItem, Select, Tab as MuiTab, Tabs as MuiTabs } from '@mui/material';
import { useLocation, useNavigate } from 'react-router-dom';
import { Paths } from '@/common/const';
import { useHttpContext } from '@/common/hooks/HttpContext';
import { toasts } from '@/common/toasts';
import { Organization, OrganizationForReporting } from '@/common/types';
import { a11yProps } from '@/common/utils';
import { LoadingSpinner } from '@/components/common/LoadingSpinner/LoadingSpinner';
import { resultIsError } from '@/services/HttpService';
import { ReportingLevel1Tab } from './ReportingLevel1Tab';
import { ReportingLevel2Tab } from './ReportingLevel2Tab';
import { ReportingLocationsTab } from './ReportingLocationsTab';
import { ReportingThirdPartyTab } from './ReportingThirdPartyTab';

interface Props {
    organizationId: number | null;
}

export const AdminReportingContainer = ({ organizationId: orgIdFromRoute }: Props) => {
    const { httpService } = useHttpContext();
    const navigate = useNavigate();
    const location = useLocation();
    const { errorToast } = toasts;
    const [isLoading, setIsLoading] = useState(false);

    const restoredOrgId = (location.state as { organizationId?: number } | null)?.organizationId ?? null;
    const initialOrgId = orgIdFromRoute ?? restoredOrgId;
    const [organizations, setOrganizations] = useState<Organization[]>([]);
    const [selectedOrganizationId, setSelectedOrganizationId] = useState<number | null>(initialOrgId);

    const [currentTab, setCurrentTab] = useState(0);
    const [reportingOrg, setReportingOrg] = useState<OrganizationForReporting | null>(null);

    const handleTabChange = (_: SyntheticEvent, newValue: number) => {
        setCurrentTab(newValue);
    };

    const getOrganizations = async () => {
        setIsLoading(true);
        const res = await httpService.getOrganizations();
        if (resultIsError(res)) {
            errorToast('Could not fetch organizations.');
        } else {
            const orderedByName = res.sort((a, b) => {
                return a.name.localeCompare(b.name);
            });
            setOrganizations(orderedByName);
        }
        setIsLoading(false);
    };

    const getOrganizationReporting = async () => {
        if (selectedOrganizationId !== null) {
            const res = await httpService.getOrganizationForReporting(selectedOrganizationId);
            if (!resultIsError(res)) {
                setReportingOrg(res);
            }
        }
    };

    useEffect(() => {
        (async () => {
            await getOrganizations();
        })();
    }, []);

    useEffect(() => {
        (async () => {
            await getOrganizationReporting();
        })();
    }, [selectedOrganizationId]);

    const handleChange = (event) => {
        const newOrgId = event.target.value;
        setSelectedOrganizationId(newOrgId);
        setCurrentTab(0);
        navigate(`${Paths.adminReportingOrganizations}${newOrgId}`);
    };

    return (
        <div>
            {isLoading ? (
                <LoadingSpinner />
            ) : (
                organizations && (
                    <>
                        <FormControl style={{ minWidth: 300 }}>
                            <InputLabel id="org-select-label">Organization</InputLabel>
                            <Select
                                labelId="org-select-label"
                                id="org-select"
                                value={selectedOrganizationId}
                                label="Organization"
                                onChange={handleChange}
                            >
                                {organizations.map((org) => (
                                    <MenuItem value={org.id} key={org.id}>
                                        {org.name}
                                    </MenuItem>
                                ))}
                            </Select>
                        </FormControl>
                        {selectedOrganizationId !== null && (
                            <>
                                <MuiTabs value={currentTab} onChange={handleTabChange} sx={{ marginTop: 3 }}>
                                    <MuiTab label="Locations" {...a11yProps(0)} />
                                    <MuiTab label={reportingOrg?.levelOneLabel ?? 'Level 1'} {...a11yProps(1)} />
                                    <MuiTab label={reportingOrg?.levelTwoLabel ?? 'Level 2'} {...a11yProps(2)} />
                                    <MuiTab label="Third Party" {...a11yProps(3)} />
                                </MuiTabs>
                                {currentTab === 0 && (
                                    <ReportingLocationsTab
                                        organizationId={selectedOrganizationId}
                                        reportingOrg={reportingOrg}
                                        onReportingOrgChange={(updated) =>
                                            setReportingOrg((prev) => ({ ...prev, ...updated }))
                                        }
                                    />
                                )}
                                {currentTab === 1 && (
                                    <ReportingLevel1Tab
                                        organizationId={selectedOrganizationId}
                                        records={reportingOrg?.dashboardMetaLevel1Records ?? []}
                                        onDelete={getOrganizationReporting}
                                        onCreate={getOrganizationReporting}
                                    />
                                )}
                                {currentTab === 2 && (
                                    <ReportingLevel2Tab
                                        organizationId={selectedOrganizationId}
                                        records={reportingOrg?.dashboardMetaLevel2Records ?? []}
                                        onDelete={getOrganizationReporting}
                                        onCreate={getOrganizationReporting}
                                    />
                                )}
                                {currentTab === 3 && (
                                    <ReportingThirdPartyTab
                                        organizationId={selectedOrganizationId}
                                        records={reportingOrg?.dashboardMetaThirdPartyRecords ?? []}
                                        onDelete={getOrganizationReporting}
                                        onCreate={getOrganizationReporting}
                                    />
                                )}
                            </>
                        )}
                    </>
                )
            )}
        </div>
    );
};
